/**
 * @file lsm_api.js
 * @description Utilities for fetching Bible verses from Living Stream Ministry API via Firebase Functions
 * Includes browser caching with 2-day expiration for improved performance
 */

import { bookNameToChapterCounts, verseCounts } from "../constants";

const CACHE_KEY_PREFIX = 'lsm_verses_cache';
const CACHE_EXPIRATION_MS = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds
const MAX_CACHE_ENTRIES = 200;

/**
 * Generate a cache key for the given parameters
 * @param {string} verses - Verse references
 * @param {string} inputMode - Input mode ('default' or 'osis')
 * @param {string} language - Bible language
 * @returns {string} - Cache key
 */
function generateCacheKey(verses, inputMode, language) {
  const normalizedParams = `${verses.trim()}_${inputMode}_${language}`;
  // Buffer is not defined in all browsers, so we use btoa instead. TODO: fix because btoa is deprecated.
  return `${CACHE_KEY_PREFIX}_${btoa(normalizedParams).replace(/[+/=]/g, '')}`;
}

/**
 * Store data in browser cache with expiration
 * @param {string} cacheKey - Cache key
 * @param {Object} data - Data to cache
 */
function setCacheData(cacheKey, data) {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      expires: Date.now() + CACHE_EXPIRATION_MS
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    pruneCache();
  } catch (error) {
    console.warn('Failed to cache LSM API response:', error);
  }
}

/**
 * Drop the oldest cached responses once the cache exceeds its entry limit
 * @param {number} maxEntries - Maximum number of cached responses to keep
 */
function pruneCache(maxEntries = MAX_CACHE_ENTRIES) {
  const entries = Object.keys(localStorage)
    .filter(key => key.startsWith(CACHE_KEY_PREFIX))
    .map(key => {
      try {
        return { key, timestamp: JSON.parse(localStorage.getItem(key)).timestamp };
      } catch (error) {
        return { key, timestamp: 0 };
      }
    });
  if (entries.length <= maxEntries) {
    return;
  }
  entries
    .sort((first, second) => first.timestamp - second.timestamp)
    .slice(0, entries.length - maxEntries)
    .forEach(entry => localStorage.removeItem(entry.key));
}

/**
 * Retrieve data from browser cache if not expired
 * @param {string} cacheKey - Cache key
 * @returns {Object|null} - Cached data or null if not found/expired
 */
function getCacheData(cacheKey) {
  try {
    const cachedItem = localStorage.getItem(cacheKey);
    if (!cachedItem) {
      return null;
    }
    
    const cacheEntry = JSON.parse(cachedItem);
    
    // Check if cache has expired
    if (Date.now() > cacheEntry.expires) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return cacheEntry.data;
  } catch (error) {
    console.warn('Failed to retrieve cached LSM API response:', error);
    return null;
  }
}

/**
 * Fetch verses from LSM API via Firebase Function proxy
 * @param {string} verses - Verse references in default format (e.g. 'Prov. 29:18; Acts 26:19')
 * @param {string} inputMode - 'default' or 'osis' format
 * @param {string} language - Bible language ('english', 'spanish', 'simplified-chinese', 'traditional-chinese')
 * @returns {Promise<Object>} - LSM API response data
 */
export async function fetchLSMVerses(verses, inputMode = 'default', language = 'english') {
  // Check cache first
  const cacheKey = generateCacheKey(verses, inputMode, language);
  const cachedData = getCacheData(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  try {
    // Just use the production URL to better simulate production
    // const baseUrl = 'https://us-central1-memorize-life.cloudfunctions.net';
    // Determine the function URL based on environment
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://us-central1-memorize-life.cloudfunctions.net'
      : 'http://localhost:5001/memorize-life/us-central1';
    
    const response = await fetch(`${baseUrl}/fetchLSMVerses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        verses,
        inputMode,
        language
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`LSM API request failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    
    // Cache the successful result
    setCacheData(cacheKey, data);
    
    return data;
    
  } catch (error) {
    console.error('Error fetching LSM verses:', error);
    throw error;
  }
}

/**
 * Convert book and chapter to LSM-compatible verse reference
 * @param {string} book - Book name (e.g. 'genesis', 'matthew')
 * @param {number} chapter - Chapter number
 * @returns {string} - Formatted verse reference for LSM API
 */
export function formatVerseReference(book, chapter) {
  // Mapping of our book names to LSM format
  const bookMapping = {
    'genesis': 'Gen.',
    'exodus': 'Ex.',
    'leviticus': 'Lev.',
    'numbers': 'Num.',
    'deuteronomy': 'Deut.',
    'joshua': 'Josh.',
    'judges': 'Judg.',
    'ruth': 'Ruth',
    '1 samuel': '1 Sam.',
    '2 samuel': '2 Sam.',
    '1 kings': '1 Kings',
    '2 kings': '2 Kings',
    '1 chronicles': '1 Chron.',
    '2 chronicles': '2 Chron.',
    'ezra': 'Ezra',
    'nehemiah': 'Neh.',
    'esther': 'Esther',
    'job': 'Job',
    'psalms': 'Psa.',
    'proverbs': 'Prov.',
    'ecclesiastes': 'Eccl.',
    'song of songs': 'S.S.',
    'isaiah': 'Isa.',
    'jeremiah': 'Jer.',
    'lamentations': 'Lam.',
    'ezekiel': 'Ezek.',
    'daniel': 'Dan.',
    'hosea': 'Hosea',
    'joel': 'Joel',
    'amos': 'Amos',
    'obadiah': 'Obad.',
    'jonah': 'Jonah',
    'micah': 'Micah',
    'nahum': 'Nahum',
    'habakkuk': 'Hab.',
    'zephaniah': 'Zeph.',
    'haggai': 'Hag.',
    'zechariah': 'Zech.',
    'malachi': 'Mal.',
    'matthew': 'Matt.',
    'mark': 'Mark',
    'luke': 'Luke',
    'john': 'John',
    'acts': 'Acts',
    'romans': 'Rom.',
    '1 corinthians': '1 Cor.',
    '2 corinthians': '2 Cor.',
    'galatians': 'Gal.',
    'ephesians': 'Eph.',
    'philippians': 'Phil.',
    'colossians': 'Col.',
    '1 thessalonians': '1 Thes.',
    '2 thessalonians': '2 Thes.',
    '1 timothy': '1 Tim.',
    '2 timothy': '2 Tim.',
    'titus': 'Titus',
    'philemon': 'Philem.',
    'hebrews': 'Heb.',
    'james': 'James',
    '1 peter': '1 Pet.',
    '2 peter': '2 Pet.',
    '1 john': '1 John',
    '2 john': '2 John',
    '3 john': '3 John',
    'jude': 'Jude',
    'revelation': 'Rev.'
  };

  const normalizedBook = book.toLowerCase();
  const lsmBook = bookMapping[normalizedBook] || book;

  // Single-chapter books must use verse ranges; "Philem. 1" returns only verse 1.
  if (bookNameToChapterCounts[normalizedBook] === 1) {
    const expectedVerseCount = verseCounts[normalizedBook]?.[chapter - 1];
    if (expectedVerseCount) {
      return `${lsmBook} ${chapter}:1-${expectedVerseCount}`;
    }
  }

  return `${lsmBook} ${chapter}`;
}

/**
 * Fetch a complete chapter from LSM API
 * @param {string} book - Book name
 * @param {number} chapter - Chapter number
 * @returns {Promise<Object>} - LSM API response with chapter content
 */
export async function fetchChapter(book, chapter) {
  const verseRef = formatChapterRangeReference(book, chapter);
  return await fetchLSMVerses(verseRef, 'default');
}

/**
 * Reverse mapping from LSM abbreviations to full book names
 */
export const lsmAbbreviationToFullName = {
  'Gen.': 'Genesis',
  'Ex.': 'Exodus',
  'Lev.': 'Leviticus',
  'Num.': 'Numbers',
  'Deut.': 'Deuteronomy',
  'Josh.': 'Joshua',
  'Judg.': 'Judges',
  'Ruth': 'Ruth',
  '1 Sam.': '1 Samuel',
  '2 Sam.': '2 Samuel',
  '1 Kings': '1 Kings',
  '2 Kings': '2 Kings',
  '1 Chron.': '1 Chronicles',
  '2 Chron.': '2 Chronicles',
  'Ezra': 'Ezra',
  'Neh.': 'Nehemiah',
  'Esther': 'Esther',
  'Job': 'Job',
  'Psa.': 'Psalms',
  'Prov.': 'Proverbs',
  'Eccl.': 'Ecclesiastes',
  'S.S.': 'Song of Songs',
  'Isa.': 'Isaiah',
  'Jer.': 'Jeremiah',
  'Lam.': 'Lamentations',
  'Ezek.': 'Ezekiel',
  'Dan.': 'Daniel',
  'Hosea': 'Hosea',
  'Joel': 'Joel',
  'Amos': 'Amos',
  'Obad.': 'Obadiah',
  'Jonah': 'Jonah',
  'Micah': 'Micah',
  'Nahum': 'Nahum',
  'Hab.': 'Habakkuk',
  'Zeph.': 'Zephaniah',
  'Hag.': 'Haggai',
  'Zech.': 'Zechariah',
  'Mal.': 'Malachi',
  'Matt.': 'Matthew',
  'Mark': 'Mark',
  'Luke': 'Luke',
  'John': 'John',
  'Acts': 'Acts',
  'Rom.': 'Romans',
  '1 Cor.': '1 Corinthians',
  '2 Cor.': '2 Corinthians',
  'Gal.': 'Galatians',
  'Eph.': 'Ephesians',
  'Phil.': 'Philippians',
  'Col.': 'Colossians',
  '1 Thes.': '1 Thessalonians',
  '2 Thes.': '2 Thessalonians',
  '1 Tim.': '1 Timothy',
  '2 Tim.': '2 Timothy',
  'Titus': 'Titus',
  'Philem.': 'Philemon',
  'Heb.': 'Hebrews',
  'James': 'James',
  '1 Pet.': '1 Peter',
  '2 Pet.': '2 Peter',
  '1 John': '1 John',
  '2 John': '2 John',
  '3 John': '3 John',
  'Jude': 'Jude',
  'Rev.': 'Revelation'
};

/**
 * Convert LSM abbreviation to full book name
 * @param {string} abbreviation - LSM book abbreviation (e.g. 'Gen.', 'Matt.')
 * @returns {string} - Full book name (e.g. 'Genesis', 'Matthew')
 */
export function getFullBookName(abbreviation) {
  return lsmAbbreviationToFullName[abbreviation] || abbreviation;
}

const normalizeBookKey = (book) => (
  String(getFullBookName(book) || '').trim().toLowerCase()
);

export function formatChapterRangeReference(book, chapter) {
  const chapterReference = formatVerseReference(book, chapter);
  if (chapterReference.includes(':')) {
    return chapterReference;
  }
  const verseCount = verseCounts[normalizeBookKey(book)]?.[Number(chapter) - 1];
  return verseCount
    ? `${chapterReference}:1-${verseCount}`
    : chapterReference;
}

export function formatCopiedVerseReference(book, chapter, verse) {
  const chapterReference = formatVerseReference(book, chapter);
  if (bookNameToChapterCounts[normalizeBookKey(book)] === 1) {
    return `${chapterReference.replace(/\s+\d+(?::\d+-\d+)?$/, '')} ${verse}`;
  }
  return `${chapterReference}:${verse}`;
}

/**
 * Fetch multiple chapters for a reading plan with exponential backoff
 * @param {Array} readings - Array of [book, chapter] pairs
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} timeout - Total timeout in milliseconds (default: 7000)
 * @param {string} language - Bible language ('english', 'spanish', 'simplified-chinese', 'traditional-chinese')
 * @returns {Promise<Object>} - Combined LSM API response
 */
export async function fetchReadings(readings, maxRetries = 3, timeout = 7000, language = 'english') {
  if (!readings || readings.length === 0) {
    return null;
  }

  // Convert readings to verse references
  const verseRefs = readings.map(([book, chapter]) =>
    formatChapterRangeReference(book, chapter)
  );
  
  // Join with semicolons for LSM API
  const versesString = verseRefs.join('; ');
  
  return await fetchLSMVersesWithBackoff(versesString, 'default', maxRetries, timeout, language);
}

/**
 * Fetch LSM verses with exponential backoff and timeout
 * @param {string} verses - Verse references
 * @param {string} inputMode - Input mode ('default' or 'osis')
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} timeout - Total timeout in milliseconds
 * @param {string} language - Bible language ('english', 'spanish', 'simplified-chinese', 'traditional-chinese')
 * @returns {Promise<Object>} - LSM API response
 */
export async function fetchLSMVersesWithRetry(verses, inputMode = 'default', language = 'english', maxRetries = 3, timeout = 7000) {
  return fetchLSMVersesWithBackoff(verses, inputMode, maxRetries, timeout, language);
}

async function fetchLSMVersesWithBackoff(verses, inputMode = 'default', maxRetries = 3, timeout = 7000, language = 'english') {
  // Check cache first
  const cacheKey = generateCacheKey(verses, inputMode, language);
  const cachedData = getCacheData(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  const startTime = Date.now();
  const isDevelopment = process.env.NODE_ENV !== 'production';
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Check if we've exceeded the total timeout
    if (Date.now() - startTime >= timeout) {
      throw new Error('Request timeout: LSM API took longer than 7 seconds');
    }
    
    try {
      // Calculate remaining time for this attempt
      const remainingTime = timeout - (Date.now() - startTime);
      const attemptTimeout = Math.min(remainingTime, 3000); // Max 3 seconds per attempt
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), attemptTimeout);
      
      try {
        const result = await fetchLSMVersesWithTimeout(verses, inputMode, controller.signal, language);
        clearTimeout(timeoutId);
        
        // Cache the successful result
        setCacheData(cacheKey, result);
        
        return result;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
      
    } catch (error) {
      // Special handling for development mode connection refused errors
      if (isDevelopment && (
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        (error.message.includes('fetch') && error.message.includes('localhost:5001'))
      )) {
        throw new Error('Failed to connect to emulators, did you run firebase emulators:start?');
      }
      
      // If this is the last attempt or we've exceeded timeout, throw the error
      if (attempt === maxRetries || Date.now() - startTime >= timeout) {
        throw error;
      }
      
      // Don't retry connection refused errors in development
      if (isDevelopment && error.message.includes('ERR_CONNECTION_REFUSED')) {
        throw error;
      }
      
      // Calculate exponential backoff delay (100ms, 200ms, 400ms, etc.)
      const delay = Math.min(100 * Math.pow(2, attempt), 1000);
      
      // Check if delay would exceed timeout
      if (Date.now() - startTime + delay >= timeout) {
        throw error;
      }
      
      console.log(`LSM API attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error.message);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

/**
 * Fetch LSM verses with abort signal support
 * @param {string} verses - Verse references
 * @param {string} inputMode - Input mode
 * @param {AbortSignal} signal - Abort signal for timeout
 * @param {string} language - Bible language ('english', 'spanish', 'simplified-chinese', 'traditional-chinese')
 * @returns {Promise<Object>} - LSM API response
 */
async function fetchLSMVersesWithTimeout(verses, inputMode, signal, language = 'english') {
  // Just use the production URL to better simulate production
  // const baseUrl = 'https://us-central1-memorize-life.cloudfunctions.net';
  // Determine the function URL based on environment
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://us-central1-memorize-life.cloudfunctions.net'
    : 'http://localhost:5001/memorize-life/us-central1';
  
  const response = await fetch(`${baseUrl}/fetchLSMVerses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      verses,
      inputMode,
      language
    }),
    signal
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`LSM API request failed: ${response.status} ${errorText}`);
  }

  return await response.json();
}
